import { Investors } from '@components/book-trial-class-jp/investors'
import CodingCertificate from '@components/common/coding-certificate'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import ClassCurriculum from '@components/course-landing-page/class-curriculum'
import ContentCard from '@components/course-landing-page/content-card'
import CourseDescription from '@components/course-landing-page/course-description'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import { COURSE_NAMES, FAQ } from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { formatSeoConfig } from '@lib/seo'
import { Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'

const CourseCard = dynamic(() =>
  import('@components/course-landing-page/course-card')
)

const { AI_AND_ML } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['aigc'].trialCourseQuery}`

const AIandML = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  useEffect(() => {
    const handleScroll = () => setShouldUpdateCourseCard(window.scrollY > 600)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useStoreUTMParams()

  const aiAndMl = formatSeoConfig({
    title: 'Best Online AI & Machine Learning Classes for Kids & Teens',
    description:
      'Codingal offers fun machine learning & AI classes for kids & teens using the K12 Computer Science Framework. Sign up today to get a free class!',
    url: 'https://www.codingal.com/courses/ai-and-machine-learning/',
    metaImage:
      'https://fastly.codingal.com/images/courses/og/ai-and-ml-course.jpg',
  })

  return (
    <>
      <NextSeo {...aiAndMl} />
      <FAQPageJsonLd
        mainEntity={FAQ[AI_AND_ML].map(
          ({ acceptedAnswerText, questionName }) => ({
            acceptedAnswerText: acceptedAnswerText,
            questionName: questionName,
          })
        )}
      />
      <CourseJsonLd
        courseName="Learn Artificial Intelligence with the Top ML and AI Courses for Kids & Teens"
        description="Our AI and Machine Learning courses introduce the exciting world of AI to students in grades 3-12. Through engaging, hands-on projects, kids explore real-life applications of AI! We offer two courses—AI Genius for kids and AI Expert for teens. Learners will discover concepts used in virtual assistants, self-driving cars, healthcare, and more. These courses make complex ideas easy to understand, spark curiosity, and build skills for the future!"
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT44H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        shouldHideCourseCard={shouldUpdateCourseCard}
        courseName={AI_AND_ML}
        urlQuery={urlQuery}
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={AI_AND_ML} />
          <ClassCurriculum
            courseCodes={['aigc', 'aiepc']}
            courseName={AI_AND_ML}
            title="Our AI class curriculum"
            description={
              <Trans>
                If your child is a beginner and eager to dive into the world of
                Artificial Intelligence and Machine Learning, we recommend
                starting with the{' '}
                <span className="font-600">
                  AI Genius - ChatGPT, AI-powered Apps & Games
                </span>{' '}
                Course. This engaging program introduces kids to AI concepts
                using fun platforms like Scratch, Thunkable, Machine Learning
                for Kids, EduBlocks and so on, where they will create
                interactive AI-powered art, stories, games, and mobile
                applications.
                <br />
                If your child already knows the fundamentals of Python and wants
                to level up their skills, they can opt for our{' '}
                <span className="font-600"></span> Course. This engaging program
                helps them expand their Python knowledge while learning to build
                real-world projects using OpenCV, Google AI Studio, and LLMs
                available on Hugging Face.
              </Trans>
            }
          />
          <Activities courseName={AI_AND_ML} />
          <Teachers
            urlQuery={urlQuery}
            title={
              <Trans>
                Learn AI and ML from experienced teachers. Our hand-picked
                coding tutors ensure the best learning experience.
              </Trans>
            }
            description={
              <Trans>
                All our instructors have a background in Computer Science and
                undergo rigorous training, contributing to our outstanding NPS
                score of 86. Teachers receive a rating of 4.9 out of 5 from
                students and parents, while the curriculum content is rated 4.8
                out of 5. With a teacher ratio of 91% female to 10% male, we
                provide a comfortable learning environment. Our tutors
                understand student&apos;s needs and offer personalized
                attention, even in group classes. Additionally, students have
                access to 24/7 doubt resolution with an expert tutor whenever
                they need assistance.
              </Trans>
            }
          />
          <CourseFeatures
            courseTitle={AI_AND_ML}
            title={
              <Trans>
                An engaging Artificial Intelligence class for kids & teens
              </Trans>
            }
          />

          <ContentCard
            title={
              <Trans>
                The best place for kids to learn the AI & ML basics and
                fundamentals
              </Trans>
            }
            description={
              <Trans>
                <span className="block">
                  Kids start with block-based programming and explore AI models
                  for text, sound, object, and emotion recognition. They create
                  chatbots with voice and text recognition, train AI models for
                  image, text, and sound, and build AI-powered apps like
                  translators, speech-to-text tools, and barcode scanners. They
                  also learn basic Python programming and build a chatbot using
                  OpenAI.
                </span>
                <br />
                <span className="block">
                  For advanced learners, the curriculum shifts to text-based
                  programming, focusing on image processing with OpenCV, and
                  AI-powered visual projects. Students analyze data, build and
                  use image classifiers LLMs, and explore feature extraction to
                  enhance classification accuracy. They use Hugging Face for
                  text analysis, create smart chatbots with ChatGPT using LLMs,
                  implement text translation and generation, and develop prompt
                  engineering skills, culminating in designing their own AI
                  applications.
                </span>
                <br />
                <span className="block">
                  Codingal offers the best gamified learning experience in AI
                  and machine learning, with expert teachers guiding kids and
                  teens every step of the way!
                </span>
              </Trans>
            }
          />
          <StemAccredited
            urlQuery={urlQuery}
            heading="STEM.org accredited educational experience and certificates"
            description="At Codingal, we are proud to be a STEM.org-accredited coding school, utilizing scientific teaching methods based on Bloom's Taxonomy, BIDE, and STEAM principles. Our curriculum is meticulously aligned with Common Core, state standards, and Advanced Placement (AP) curricula, ensuring students receive a comprehensive and relevant education. We also cover the K-12 Computer Science Framework, providing developmentally appropriate content that equips learners with essential skills for a technology-driven world."
          />
          <CodingCertificate
            title="Unlock your skills and earn your AI & ML certifications"
            description="In this specialized AI & ML course, learners from grades 3 to 12 can earn STEM.org-certified AI & ML coding certificates—AI Genius and AI Expert—that celebrate their achievements. As they progress, students unlock special badges and leaderboard rankings, making programming a fun and rewarding experience. These certifications are easy to download and share, are STEM.org accredited, and can be added to your child's learning portfolio for showcasing their accomplishments with the world and on social media."
            imagePath="/images/courses/ai-and-ml/cetrificate"
            imageWidth={230}
            imageHeight={195}
          />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
          />
          <TrustpilotReview shouldScrollToTop isCoursepage />
          <CodingCertificate
            title="Our expert tutors help students master AI and NLP"
            description="Our expert tutors are dedicated to helping students master artificial intelligence (AI) and natural language processing (NLP). With personalized guidance and hands-on support, they make complex concepts easy to understand. Whether students are beginners or looking to deepen their knowledge, our tutors provide tailored lessons that build confidence and skills. Through engaging projects and real-world applications, students gain a solid foundation in AI and NLP, empowering them to explore new technologies and innovate in their learning journey."
            imagePath="/images/courses/ai-and-ml/ai-course"
            imageWidth={213}
            imageHeight={219}
          />
          <CodingForStudents
            title="Our Artificial Intelligence course is appropriate for Middle school and High school students"
            description="Our AI & ML course is designed for both middle school and high school students in grades 3 to 12. Codingal supports kids ages 6 to 18 and grades K to 12 by providing engaging and comprehensive coding education that fosters creativity and critical thinking skills. We create a dynamic learning environment that empowers students to explore the exciting world of coding and artificial intelligence."
            imagePath="/images/courses/ai-and-ml/ai-course-students"
            imageWidth={235}
            imageHeight={241}
          />
          <Faq
            courseName={AI_AND_ML}
            title="Frequently Asked Questions for AI & Machine Learning courses at Codingal"
          />
          <Investors isCoursePage urlQuery={urlQuery} />
          <RecommendedCourses
            title="Recommended courses for you"
            courseCodes={['sp', 'gd', 'ad', 'wd', 'pc']}
          />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={AI_AND_ML}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <FooterBanner
        title="AI & Machine Learning for kids with the best Computer Science teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default AIandML
